import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { findCompanyBySlug } from '../../../client-experience/utils/companyAccess';
import { getCompanyAppointmentUrlSegment, isFirestoreAppointmentDocId } from '../../../shared/booking/appointments/companyAppointmentRouting';
import { getStorefrontAppointmentDetailPath } from '../../storefront-client-portal/utils/clientPortalPaths';
import { getStorefrontBookingConfirmationPath } from '../../storefront-booking/storefrontBookingPaths';
import { useStorefrontAuth } from '../../auth/context/StorefrontAuthContext';
import { getPublicAppointmentOrNull } from '../../../api/storefront/storefront';
import '../../styles/LandingPage.css';
import '../../payments/storefront-booking/storefront-booking-payment.css';

const convertToDate = (dateValue) => {
  if (!dateValue) return null;

  try {
    if (dateValue instanceof Date) return dateValue;
    if (dateValue?.toDate && typeof dateValue.toDate === 'function') return dateValue.toDate();
    if (dateValue?.seconds) return new Date(dateValue.seconds * 1000);

    const date = new Date(dateValue);
    return Number.isNaN(date.getTime()) ? null : date;
  } catch {
    return null;
  }
};

const formatCurrency = (amount) => {
  const value = Number(amount);
  if (!Number.isFinite(value)) return null;
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
  }).format(value);
};

const normalizeSummary = (summary, appointmentRef) => {
  if (!summary) return null;

  return {
    id: summary.id || appointmentRef,
    serviceName: summary.serviceName || '',
    selectedPackage: summary.selectedPackage || null,
    selectedAddons: Array.isArray(summary.selectedAddons) ? summary.selectedAddons : [],
    duration: summary.duration || null,
    dateTime: summary.dateTime || null,
    employeeName: summary.employeeName || '',
    totalPrice: summary.totalPrice,
    paymentStatus: summary.paymentStatus || 'unpaid',
    publicNumber: summary.publicNumber ?? null,
  };
};

/**
 * Shown after a storefront booking is confirmed without online payment.
 * Route: /book/:companyUrl/booking/confirmed/:appointmentRef
 */
const StorefrontBookingConfirmationPage = () => {
  const { companyUrl, appointmentId: appointmentRef } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPortalAccess } = useStorefrontAuth();
  const initialSummary = useMemo(
    () => normalizeSummary(location.state?.summary, appointmentRef),
    [appointmentRef, location.state?.summary]
  );
  const [loading, setLoading] = useState(!initialSummary);
  const [error, setError] = useState(null);
  const [appointment, setAppointment] = useState(initialSummary);

  useEffect(() => {
    if (initialSummary) {
      setAppointment(initialSummary);
      setLoading(false);
    }
  }, [initialSummary]);

  useEffect(() => {
    if (!appointmentRef) return undefined;

    let cancelled = false;
    let retryTimer;

    const load = async (attempt = 0) => {
      try {
        const company = await findCompanyBySlug(companyUrl);
        if (!company?.id) {
          throw new Error('Business not found.');
        }

        const loaded = await getPublicAppointmentOrNull({
          companyId: company.id,
          id: appointmentRef,
        });

        if (!loaded) {
          if (initialSummary) {
            return;
          }
          if (attempt < 4) {
            retryTimer = setTimeout(() => load(attempt + 1), 1500);
            return;
          }
          throw new Error('Appointment not found yet. Please check your confirmation email.');
        }

        if (!cancelled) {
          setAppointment((current) => ({
            ...(current || {}),
            ...loaded,
            id: loaded.id,
          }));
          setLoading(false);
          setError(null);
        }
      } catch (e) {
        if (!cancelled && !initialSummary) {
          setError(e?.message || 'Something went wrong loading your booking.');
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [appointmentRef, companyUrl, initialSummary]);

  useEffect(() => {
    if (!appointment || !companyUrl || !appointmentRef) return;

    const segment = getCompanyAppointmentUrlSegment(appointment);
    if (!segment || segment === appointmentRef) return;
    if (isFirestoreAppointmentDocId(appointmentRef) && !isFirestoreAppointmentDocId(segment)) {
      navigate(getStorefrontBookingConfirmationPath(companyUrl, appointment), {
        replace: true,
        state: location.state,
      });
    }
  }, [appointment, appointmentRef, companyUrl, location.state, navigate]);

  const appointmentWhen = useMemo(() => {
    const date = convertToDate(appointment?.dateTime);
    if (!date) return null;
    return format(date, "EEEE, MMMM d, yyyy 'at' h:mm a");
  }, [appointment?.dateTime]);

  const appointmentRefLabel = useMemo(() => {
    const pub = appointment?.publicNumber;
    if (pub != null && /^\d+$/.test(String(pub))) {
      return `Appointment #${pub}`;
    }
    return null;
  }, [appointment?.publicNumber]);

  const paymentNote = useMemo(() => {
    const total = Number(appointment?.totalPrice);
    if (!Number.isFinite(total) || total <= 0.01) {
      return 'No payment required for this appointment.';
    }
    return 'Payment is due at the time of your appointment.';
  }, [appointment?.totalPrice]);

  const totalLabel = formatCurrency(appointment?.totalPrice);
  const viewAppointmentHref =
    hasPortalAccess && appointment && companyUrl
      ? getStorefrontAppointmentDetailPath(companyUrl, appointment)
      : null;

  const title = loading ? 'Confirming your booking…' : error ? 'Almost there' : "You're all set";

  return (
    <div className="landing-page">
      <div className="container storefront-booking-success">
        <div className="storefront-booking-success__card">
          <h1>{title}</h1>

          {loading ? (
            <p>Please wait a moment while we load your appointment details.</p>
          ) : null}

          {!loading && error ? (
            <>
              <p>{error}</p>
              <Link to={`/book/${companyUrl}`} className="back-link">
                Back to booking site
              </Link>
            </>
          ) : null}

          {!loading && !error && appointment ? (
            <>
              {appointmentRefLabel ? (
                <p className="storefront-booking-success__ref">{appointmentRefLabel}</p>
              ) : null}

              <p>
                Your appointment is confirmed. A confirmation email will be sent shortly with the
                details below.
              </p>

              <div className="storefront-payment-step__summary storefront-booking-success__summary">
                <h4 className="storefront-payment-step__summary-title">Appointment summary</h4>
                <dl className="storefront-payment-step__summary-list">
                  {appointment.serviceName ? (
                    <div className="storefront-payment-step__summary-row">
                      <dt>Service</dt>
                      <dd>{appointment.serviceName}</dd>
                    </div>
                  ) : null}
                  {appointment.selectedPackage?.name ? (
                    <div className="storefront-payment-step__summary-row">
                      <dt>Package</dt>
                      <dd>{appointment.selectedPackage.name}</dd>
                    </div>
                  ) : null}
                  {appointment.selectedAddons?.length ? (
                    <div className="storefront-payment-step__summary-row storefront-payment-step__summary-row--addons">
                      <dt>Add-ons</dt>
                      <dd>
                        <ul className="storefront-payment-step__addon-list">
                          {appointment.selectedAddons.map((addon) => (
                            <li key={addon.id || addon.name}>{addon.name}</li>
                          ))}
                        </ul>
                      </dd>
                    </div>
                  ) : null}
                  {appointment.duration ? (
                    <div className="storefront-payment-step__summary-row">
                      <dt>Duration</dt>
                      <dd>{appointment.duration} minutes</dd>
                    </div>
                  ) : null}
                  {appointmentWhen ? (
                    <div className="storefront-payment-step__summary-row">
                      <dt>Date &amp; time</dt>
                      <dd>{appointmentWhen}</dd>
                    </div>
                  ) : null}
                  {appointment.employeeName ? (
                    <div className="storefront-payment-step__summary-row">
                      <dt>Staff</dt>
                      <dd>{appointment.employeeName}</dd>
                    </div>
                  ) : null}
                  {totalLabel ? (
                    <div className="storefront-payment-step__summary-row">
                      <dt>Total</dt>
                      <dd>{totalLabel}</dd>
                    </div>
                  ) : null}
                  <div className="storefront-payment-step__summary-row">
                    <dt>Payment</dt>
                    <dd>{paymentNote}</dd>
                  </div>
                </dl>
              </div>

              <div className="storefront-booking-success__actions">
                {viewAppointmentHref ? (
                  <Link to={viewAppointmentHref} className="back-link">
                    View appointment
                  </Link>
                ) : null}
                <Link to={`/book/${companyUrl}`} className="back-link">
                  Back to booking site
                </Link>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default StorefrontBookingConfirmationPage;
